import type { NavCountKey, Permission } from '@mevaser/shared'
import type { LucideIcon } from 'lucide-react'
import {
  ArrowLeftRight,
  Barcode,
  BookOpen,
  Boxes,
  CalendarClock,
  Cctv,
  ClipboardList,
  Clock,
  DollarSign,
  Download,
  Gift,
  Headset,
  Images,
  LayoutDashboard,
  ListChecks,
  Mail,
  MapPin,
  MessagesSquare,
  Monitor,
  Package,
  PackageSearch,
  ScanLine,
  Settings,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Smartphone,
  Tags,
  TicketPercent,
  Truck,
  Users,
  Wallet,
  Warehouse,
  Wrench,
} from 'lucide-react'

export interface NavItem {
  label: string
  to: string
  icon: LucideIcon
  /** Hidden entirely when the signed-in role lacks this. */
  permission?: Permission
  /** Also offered as an action in the command palette. */
  keywords?: string[]
  /** Which count in `/me/nav-counts` shows as a red badge on this tab. */
  badge?: NavCountKey
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

/**
 * One definition drives the sidebar and the command palette. Two lists would
 * drift, and the palette silently missing a page is the kind of bug nobody
 * reports — they just stop using the palette.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [{ label: 'Dashboard', to: '/', icon: LayoutDashboard, keywords: ['home', 'today'] }],
  },
  {
    label: 'Sell',
    items: [
      {
        label: 'Orders',
        to: '/orders',
        badge: 'orders',
        icon: ShoppingCart,
        permission: 'orders:read',
        keywords: ['sales', 'receipts'],
      },
      {
        label: 'Customers',
        to: '/customers',
        icon: Users,
        permission: 'customers:read',
        keywords: ['people', 'accounts', 'phone'],
      },
      {
        label: 'Repairs',
        to: '/repairs',
        badge: 'repairs',
        icon: Wrench,
        permission: 'repairs:read',
        keywords: ['calendar', 'tickets', 'workshop', 'schedule', 'technician'],
      },
      {
        label: 'Rentals',
        to: '/rentals',
        badge: 'rentals',
        icon: CalendarClock,
        permission: 'rentals:read',
        keywords: ['rent', 'loan', 'deposit', 'due', 'overdue', 'sim', 'imei', 'iccid'],
      },
      {
        label: 'Plans',
        to: '/plans',
        icon: Smartphone,
        permission: 'subscriptions:read',
        keywords: ['plan', 'subscription', 'renew', 'billing', 'line', 'phone line', 'recurring'],
      },
      {
        label: 'Support',
        to: '/support-tickets',
        badge: 'support',
        icon: Headset,
        permission: 'support:read',
        keywords: ['tickets', 'sms', 'texts', 'calls', 'inbox', 'inquiries', 'phone'],
      },
      {
        label: 'Tasks',
        to: '/tasks',
        badge: 'tasks',
        icon: ListChecks,
        permission: 'tasks:read',
        keywords: ['todo', 'to-do', 'follow up', 'follow-up', 'reminder', 'assign', 'due'],
      },
      {
        label: 'Mail',
        to: '/mail',
        badge: 'mail',
        icon: Mail,
        permission: 'mail:read',
        keywords: ['email', 'inbox', 'info@', 'compose'],
      },
      {
        label: 'Workspace',
        to: '/workspace',
        badge: 'workspace',
        icon: MessagesSquare,
        permission: 'workspace:read',
        keywords: [
          'chat',
          'channels',
          'boards',
          'bulletin',
          'announcements',
          'posts',
          'guests',
          'team',
          'dm',
          'direct messages',
          'private messages',
        ],
      },
      {
        label: 'Wiki',
        to: '/wiki',
        icon: BookOpen,
        permission: 'wiki:read',
        keywords: ['knowledge base', 'kb', 'docs', 'handbook', 'guides', 'articles'],
      },
    ],
  },
  {
    label: 'Inventory',
    items: [
      {
        label: 'Products',
        to: '/products',
        icon: Package,
        permission: 'catalog:read',
        keywords: ['catalog', 'sku', 'price'],
      },
      {
        label: 'Categories',
        to: '/categories',
        icon: Tags,
        permission: 'catalog:read',
        keywords: ['category', 'shelves', 'headings', 'sections', 'phones', 'accessories'],
      },
      {
        label: 'Coupons',
        to: '/coupons',
        icon: TicketPercent,
        permission: 'catalog:read',
        keywords: ['coupon', 'promo', 'discount', 'code', 'sale'],
      },
      {
        label: 'Gift cards',
        to: '/gift-cards',
        icon: Gift,
        permission: 'gift_cards:read',
        keywords: ['gift', 'card', 'voucher', 'store credit', 'balance', 'code'],
      },
      {
        label: 'Attributes',
        to: '/attributes',
        icon: SlidersHorizontal,
        permission: 'catalog:read',
        keywords: ['attribute', 'options', 'choices', 'color', 'storage', 'dropdown'],
      },
      {
        label: 'Compatibility',
        to: '/compatibility',
        icon: Smartphone,
        permission: 'catalog:read',
        keywords: [
          'devices',
          'compatible',
          'fits',
          'device tags',
          'sim',
          'plans',
          'services',
          'phone',
        ],
      },
      {
        label: 'Image library',
        to: '/image-library',
        icon: Images,
        permission: 'catalog:read',
        keywords: [
          'photos',
          'pictures',
          'media',
          'gallery',
          'uploads',
          'documents',
          'pdf',
          'word',
          'files',
        ],
      },
      {
        label: 'Intake',
        to: '/intake',
        icon: ScanLine,
        permission: 'inventory:write',
        keywords: ['scan', 'receive', 'imei', 'shipment'],
      },
      {
        label: 'Labels',
        to: '/labels',
        icon: Barcode,
        permission: 'inventory:read',
        keywords: [
          'barcode',
          'qr',
          'print',
          'label',
          'ocr',
          'imei',
          'sku',
          'sim',
          'iccid',
          'shelf',
        ],
      },
      {
        label: 'Provisioning',
        to: '/provisioning',
        badge: 'provisioning',
        icon: Boxes,
        permission: 'inventory:read',
        keywords: ['kosher', 'queue', 'filter', 'devices'],
      },
      {
        label: 'Stock',
        to: '/stock',
        badge: 'stock',
        icon: Warehouse,
        permission: 'inventory:read',
        keywords: ['levels', 'ledger', 'movements', 'accessories'],
      },
      {
        label: 'Stock requests',
        to: '/stock-requests',
        badge: 'stockRequests',
        icon: ArrowLeftRight,
        permission: 'inventory:read',
        keywords: ['transfer', 'move', 'warehouse', 'request', 'branch', 'location'],
      },
      {
        label: 'Purchasing',
        to: '/purchasing',
        icon: DollarSign,
        permission: 'inventory:read',
        keywords: [
          'costs',
          'cogs',
          'sales tax',
          'tax report',
          'invoices',
          'profit',
          'margin',
          'paid',
          'unpaid',
        ],
      },
      {
        label: 'Purchase orders',
        to: '/purchase-orders',
        icon: ClipboardList,
        permission: 'inventory:read',
        keywords: ['po', 'invoice', 'pending', 'on order', 'receive', 'incoming', 'vendor'],
      },
      {
        label: 'Vendors',
        to: '/vendors',
        icon: Truck,
        permission: 'inventory:read',
        keywords: ['supplier', 'suppliers', 'directory', 'contacts'],
      },
      {
        label: 'Locations',
        to: '/locations',
        icon: MapPin,
        permission: 'inventory:read',
        keywords: ['warehouse', 'store', 'address', 'phone', 'branch'],
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        label: 'Vending machines',
        to: '/machines',
        badge: 'machines',
        icon: PackageSearch,
        permission: 'kiosks:read',
        keywords: [
          'vend',
          'dispense',
          'tablet',
          'raspberry pi',
          'slots',
          'issues',
          'kiosk',
          'kiosks',
          'terminals',
          'fleet',
          'screens',
        ],
      },
      {
        label: 'Store kiosks',
        to: '/kiosks',
        icon: Monitor,
        permission: 'kiosks:read',
        keywords: ['kiosk', 'pairing', 'checkout', 'sign in', 'plans', 'in-store', 'browser'],
      },
      {
        label: 'Cameras',
        to: '/cameras',
        icon: Cctv,
        permission: 'kiosks:read',
        keywords: [
          'camera',
          'snapshot',
          'video',
          'clip',
          'cctv',
          'security',
          'footage',
          'surveillance',
          'recording',
        ],
      },
      {
        label: 'Downloads',
        to: '/downloads',
        icon: Download,
        permission: 'kiosks:read',
        keywords: ['apk', 'android', 'app', 'install'],
      },
      {
        label: 'Staff',
        to: '/staff',
        icon: ShieldCheck,
        permission: 'staff:read',
        keywords: ['users', 'accounts', 'roles', 'permissions', '2fa'],
      },
      {
        label: 'Time clock',
        to: '/time-clock',
        icon: Clock,
        permission: 'timeclock:manage',
        keywords: ['hours', 'timesheet', 'punch', 'shift', 'payroll', 'clock in', 'attendance'],
      },
      {
        label: 'Payments & expenses',
        to: '/payments',
        icon: Wallet,
        permission: 'expenses:read',
        keywords: [
          'pay',
          'payroll',
          'freelancer',
          'contractor',
          'zelle',
          'ach',
          'echeck',
          'check',
          'rent',
          'expense',
          'bills',
          'vendor payment',
        ],
      },
      {
        label: 'Settings',
        to: '/settings',
        icon: Settings,
        keywords: ['users', 'staff', 'roles', 'password', 'security'],
      },
    ],
  },
]

export function visibleGroups(can: (permission: Permission) => boolean): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.permission || can(item.permission)),
  })).filter((group) => group.items.length > 0)
}
